import { sentimentColorMap } from '@/pages/opinion/constants';
import {
  echartsContent,
  getPercentageInfo,
  mathFormat,
  sortMapAuxiliaryFunc,
} from '@/pages/opinion/utils';
import { formatTimeXAxisText } from '@/pages/opinion/utils/chart';
import { ServerTimeType } from '@/pages/opinion/components/FilterCommon/types';
import { getAxisPointer } from '@/pages/opinion/components/EChartCommon';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import {
  brandHealthFormat,
  sentimentFormat,
} from '@/pages/opinion/utils/value';
import {
  sentimentBrandHealthLegend,
  textShowFun,
  visualMap,
} from './commonOption';

export default function (
  dateType: ServerTimeType,
  formatMessage: any,
  showTotal: boolean = true,
) {
  return {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
      borderWidth: 0,
      borderRadius: 6,
      textStyle: {
        color: '#FFFFFF',
        fontSize: 12,
      },
      confine: true,
      hideDelay: 200,
      triggerOn: 'mousemove',
      padding: 0,
      className: 'mention-sentiment-tooltip-box',
      formatter: (params: any) => {
        let title = listTimeTitleFunc(params?.[0]?.axisValue, dateType, true);
        return formatChartParamsDate({
          title,
          params,
          textShowFun: textShowFun(formatMessage),
          recordDealwith: (record, element) => {
            if (
              element.seriesName === formatMessage({ id: 'Brand Health' }) &&
              element?.data?.flag
            ) {
              return { ...record, originValue: '-' };
            }
            return record;
          },
          dealwithRecords(records) {
            const totalItem = records.find(
              (item) => item.originText === 'total总数',
            );
            const newRecords = records
              .filter((item) => item.originText !== 'total总数')
              .map((item) => {
                if (
                  [
                    formatMessage({ id: 'Sentiment' }),
                    formatMessage({ id: 'Brand Health' }),
                  ].includes(item.originText)
                ) {
                  const isBrandHealth =
                    formatMessage({ id: 'Brand Health' }) === item.originText;
                  return {
                    ...item,
                    value: isBrandHealth
                      ? brandHealthFormat(item.originValue)
                      : sentimentFormat(item.originValue),
                  };
                }
                const percentageValue = getPercentageInfo(
                  item.originValue,
                  totalItem?.originValue || 0,
                );
                const text = ['0%', '-'].includes(percentageValue)
                  ? ''
                  : `(${percentageValue})`;
                return {
                  ...item,
                  value: `${item.value}${text}`,
                };
              });
            newRecords.sort(
              sortMapAuxiliaryFunc(
                {
                  [formatMessage({ id: 'Sentiment' })]: 0,
                  [formatMessage({ id: 'Brand Health' })]: 1,
                  [formatMessage({ id: 'Positive' })]: 2,
                  [formatMessage({ id: 'Neutral' })]: 3,
                  [formatMessage({ id: 'Negative' })]: 4,
                },
                (item) => item.originText,
              ),
            );
            newRecords.unshift({
              text: formatMessage({ id: 'Mentions' }),
              value: totalItem?.value || 0,
              originValue: totalItem?.originValue || 0,
              originText: formatMessage({ id: 'Mentions' }),
            });

            return newRecords;
          },
        });
      },
    },
    grid: {
      right: '0%',
      bottom: '28px',
      top: '20px',
      containLabel: true,
    },
    visualMap: visualMap,
    legend: {
      itemWidth: 15,
      selected: {
        [formatMessage({ id: 'Sentiment' })]: false,
      },
      data: [
        ...sentimentBrandHealthLegend(formatMessage),
        {
          name: formatMessage({ id: 'Positive' }),
          itemStyle: {
            color: sentimentColorMap[0].color,
          },
        },
        {
          name: formatMessage({ id: 'Neutral' }),
          itemStyle: {
            color: sentimentColorMap[1].color,
          },
        },
        {
          name: formatMessage({ id: 'Negative' }),
          itemStyle: {
            color: sentimentColorMap[2].color,
          },
        },
      ],
    },
    axisPointer: getAxisPointer((data) => {
      return formatTimeXAxisText(data.value, dateType || 'daily');
    }),
    xAxis: [
      {
        axisLabel: {
          formatter: (value: any) => formatTimeXAxisText(value, dateType),
        },
        data: [],
      },
    ],
    yAxis: [
      // mentions 数据
      {
        type: 'value',
        axisLabel: {
          formatter: (value: any) => echartsContent(value),
          color: '#838697',
        },
        nameTextStyle: {
          color: '#444444',
          fontWeight: 400,
          fontSize: '12px',
        },
        splitNumber: 5,
      },
      {
        type: 'value',
        axisLabel: {
          formatter: (value: any) => echartsContent(value),
          color: '#838697',
        },
        splitNumber: 5, 
      },
    ],
    series: [
      {
        name: formatMessage({ id: 'Sentiment' }),
        type: 'line',
        yAxisIndex: 1,
        itemStyle: {
          color: '#29B569',
        },
        data: [],
      },
      {
        name: formatMessage({ id: 'Brand Health' }),
        type: 'line',
        yAxisIndex: 1,
        data: [],
      },
      {
        name: 'total总数',
        type: 'bar',
        barMaxWidth: '70px', // 柱子宽度同实体柱子，否则会错位
        barGap: '-100%', // 左移100%，stack不再与需堆叠的柱子在一列
        label: {
          normal: {
            show: showTotal,
            position: 'top', // 位置设为top
            formatter: (c) => mathFormat(c?.value),
            textStyle: { color: '#444' },
          },
        },
        itemStyle: {
          normal: {
            color: 'rgba(128, 128, 128, 0)', // 为透明
          },
        },
        data: [],
      },
      {
        name: formatMessage({ id: 'Positive' }),
        type: 'bar',
        stack: 'sentiment',
        yAxisIndex: 0,
        data: [],
        itemStyle: {
          color: sentimentColorMap[0].color,
        },
        barMaxWidth: '70px',
      },
      {
        name: formatMessage({ id: 'Neutral' }),
        type: 'bar',
        stack: 'sentiment',
        yAxisIndex: 0,
        data: [],
        itemStyle: {
          color: sentimentColorMap[1].color,
        },
        barMaxWidth: '70px',
      },
      {
        name: formatMessage({ id: 'Negative' }),
        type: 'bar',
        stack: 'sentiment',
        yAxisIndex: 0,
        data: [],
        itemStyle: {
          color: sentimentColorMap[2].color,
        },
        barMaxWidth: '70px',
      },
    ],
  };
}
